/* ============================================================
   L'USINE — Révélations au défilement, ajout rapide au panier,
   carrousels et barre d'achat mobile.
   ============================================================ */
(() => {
  const root = document.documentElement;
  window.__luMotionLoaded = true;
  root.classList.remove('lu-motion-failsafe');

  /* ── Révélations ─────────────────────────────────────────── */
  // Éléments animés automatiquement, sans toucher aux sections.
  // [sélecteur, type, groupe pour l'effet en cascade]
  const TARGETS = [
    ['.lu-section-header', 'up'],
    ['.lu-products__item', 'up', '.lu-products__grid'],
    ['.lu-coll-card', 'image', '.lu-collections__grid'],
    ['.lu-features__item', 'up', '.lu-features'],
    ['.lu-story__image', 'image'],
    ['.lu-story__content > *', 'up', '.lu-story__content'],
    ['.lu-newsletter > *', 'up', '.lu-newsletter'],
    ['.product-grid > .lu-pcard', 'up', '.product-grid'],
    ['.related-products__grid > .lu-pcard', 'up', '.related-products__grid'],
    ['.lu-contact__hero > *', 'up', '.lu-contact__hero'],
    ['.lu-contact__body > *', 'up', '.lu-contact__body'],
  ];

  const motionOn = root.classList.contains('lu-motion');
  let observer;

  const tag = (scope) => {
    if (!motionOn) return;
    TARGETS.forEach(([sel, type, group]) => {
      scope.querySelectorAll(sel).forEach((el) => {
        if (el.hasAttribute('data-lu-reveal')) return;
        el.setAttribute('data-lu-reveal', type);
        if (group) {
          const parent = el.closest(group);
          const siblings = parent ? [...parent.querySelectorAll(sel)] : [el];
          // cascade limitée à 6 pas pour ne pas faire attendre
          el.style.setProperty('--lu-i', Math.min(siblings.indexOf(el), 6));
        }
        observer.observe(el);
      });
    });
  };

  if (motionOn) {
    if (!('IntersectionObserver' in window) || window.Shopify?.designMode) {
      // Dans l'éditeur de thème : tout reste visible pour pouvoir modifier.
      root.classList.add('lu-motion-failsafe');
    } else {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
      tag(document);
    }
  }

  /* ── Parallaxe douce du hero (ordinateur, intensité forte) ── */
  const heroImg = document.querySelector('.lu-hero__bg-img');
  if (
    heroImg &&
    motionOn &&
    root.dataset.luIntensity === 'strong' &&
    matchMedia('(min-width: 990px) and (prefers-reduced-motion: no-preference)').matches
  ) {
    let ticking = false;
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const y = Math.min(window.scrollY, 900);
          heroImg.style.transform = `translate3d(0, ${y * 0.18}px, 0)`;
          ticking = false;
        });
      },
      { passive: true }
    );
  }

  /* ── Carrousels (flèches ordinateur, glisser au doigt) ────── */
  const initCarousel = (wrap) => {
    const track = wrap.querySelector('[data-lu-track]');
    const prev = wrap.querySelector('[data-lu-prev]');
    const next = wrap.querySelector('[data-lu-next]');
    if (!track || !prev || !next) return;
    const step = () => {
      const item = track.firstElementChild;
      return item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0) : 300;
    };
    const update = () => {
      prev.disabled = track.scrollLeft <= 4;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    };
    prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', update, { passive: true });
    update();
  };
  document.querySelectorAll('[data-lu-carousel]').forEach(initCarousel);

  /* ── Ajout rapide au panier ───────────────────────────────── */
  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-lu-quick-add]');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    if (btn.getAttribute('aria-busy') === 'true') return;

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const body = new FormData();
    body.append('id', btn.dataset.variantId);
    body.append('quantity', '1');
    if (cart) {
      body.append('sections', cart.getSectionsToRender().map((s) => s.id));
      body.append('sections_url', window.location.pathname);
      cart.setActiveElement(btn);
    }

    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('is-loading');
    try {
      const res = await fetch(`${window.routes?.cart_add_url || '/cart/add'}`, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body,
      });
      const data = await res.json();
      if (data.status) {
        btn.classList.add('is-error');
        btn.querySelector('[data-lu-quick-add-label]').textContent = data.description || data.message;
        return;
      }
      btn.classList.add('is-added');
      setTimeout(() => btn.classList.remove('is-added'), 1800);
      if (cart) {
        cart.classList.contains('is-empty') && cart.classList.remove('is-empty');
        cart.renderContents(data);
      } else {
        window.location.href = window.routes?.cart_url || '/cart';
      }
    } catch (e) {
      window.location.href = btn.closest('a')?.href || '/cart';
    } finally {
      btn.removeAttribute('aria-busy');
      btn.classList.remove('is-loading');
    }
  });

  /* ── Barre d'achat fixe sur mobile (fiche produit) ────────── */
  const sticky = document.querySelector('[data-lu-sticky-atc]');
  const mainBtn = document.querySelector('.product-form__submit[name="add"]');
  if (sticky && mainBtn && 'IntersectionObserver' in window) {
    const stickyBtn = sticky.querySelector('[data-lu-sticky-btn]');
    const stickyPrice = sticky.querySelector('[data-lu-sticky-price]');
    const priceSource = document.querySelector('.product__info-container .price');

    const sync = () => {
      stickyBtn.disabled = mainBtn.disabled;
      const label = mainBtn.querySelector('span');
      if (label) stickyBtn.textContent = label.textContent.trim();
      const current = priceSource?.querySelector('.price-item--sale, .price-item--regular');
      if (current && stickyPrice) stickyPrice.textContent = current.textContent.trim();
    };

    new IntersectionObserver(([entry]) => {
      const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sync();
      sticky.classList.toggle('is-visible', passed);
      sticky.toggleAttribute('inert', !passed);
    }).observe(mainBtn);

    stickyBtn.addEventListener('click', () => mainBtn.click());
    new MutationObserver(sync).observe(mainBtn, { attributes: true, childList: true, subtree: true });
  }

  /* ── Éditeur de thème : réappliquer après modification ───── */
  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll('[data-lu-carousel]').forEach(initCarousel);
  });
})();
